import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Package,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import paymentMethodMasterService from '../../services/paymentMethodMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const PaymentMethodMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalMethods: 0,
    activeMethods: 0,
    inactiveMethods: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [paymentMethodsResponse, statsResponse] = await Promise.all([
        paymentMethodMasterService.getAllPaymentMethods(),
        paymentMethodMasterService.getPaymentMethodStats()
      ])
      
      if (paymentMethodsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (paymentMethodsResponse.data || []).map(paymentMethod => ({
          ...paymentMethod,
          id: paymentMethod._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (paymentMethodsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No payment method records found. Create your first payment method!')
        }
      } else {
        toast.error(paymentMethodsResponse.message || 'Failed to load payment methods')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading payment method master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view payment methods')
      } else {
        toast.error('Failed to load payment method master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'paymentMethodCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'paymentMethodName',
      header: 'Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'paymentType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'center' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'settlementDays',
      header: 'Settlement Days',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span>{getValue() || 0}</span>
        </div>
      ),
    },
    {
      accessorKey: 'processingFee',
      header: 'Processing Fee',
      cell: ({ row }) => {
        const fee = row.original.processingFee || 0
        const feeType = row.original.processingFeeType || 'Fixed'
        return (
          <div className="active-cell" style={{ textAlign: 'center' }}>
            <span>{fee > 0 ? `${fee}${feeType === 'Percentage' ? '%' : ''}` : '-'}</span>
          </div>
        )
      },
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/payment-method-master/form')
  }

  const handleEdit = (paymentMethodData) => {
    navigate('/expenses/payment-method-master/edit', { state: { paymentMethodData } })
  }

  const handleView = (paymentMethodData) => {
    navigate('/expenses/payment-method-master/view', { state: { paymentMethodData } })
  }

  const handleDelete = (paymentMethodData) => {
    setDeleteItem(paymentMethodData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting payment method...')
      // Use _id instead of id
      const response = await paymentMethodMasterService.deletePaymentMethod(deleteItem._id)
      if (response.success) {
        toast.success('Payment method deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete payment method')
      }
    } catch (error) {
      console.error('Error deleting payment method:', error)
      if (error.response?.status === 404) {
        toast.error('Payment method not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this payment method')
      } else {
        toast.error('Failed to delete payment method. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Payment Method Master',
      path: '/expenses/payment-method-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Payment Method Master</h1>
          <p>Manage payment options and reimbursement methods</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add Payment Method
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalMethods}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalMethods}</div>
          <div className="metric-title">Total Methods</div>
          <div className="metric-subtitle">All payment methods</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.activeMethods}</span>
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeMethods}</div>
          <div className="metric-title">Active Methods</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.inactiveMethods}</span>
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveMethods}</div>
          <div className="metric-title">Inactive Methods</div>
          <div className="metric-subtitle">Currently inactive</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Payment Method"
        message={`Are you sure you want to delete "${deleteItem?.paymentMethodName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PaymentMethodMaster

